import { AuxiliumComponent } from "../auxilium-component";
import { Razor } from "../../razor/razor";

/**
 * A month grid that picks dates without the native picker, so it can be styled. Everything the
 * calendar writes comes from the culture it settles on, the same way chip-dateformat works, and the
 * handful of words Intl has no answer for come from the Localizer.
 */
export default class Calendar extends AuxiliumComponent {
	static observable = [
		"value", "min", "max", "lang", "month", "first-day", "weekday-format", "disabled-dates",
		"disabled-days", "label", "helper-text", "placeholder", "size", "variation", "name",
		"hidden-name", "hidden-id", "previous-label", "next-label", "today-label", "clear-label",
		"week-label", "open-label", "choose-month-label", "choose-year-label"
	];

	static observableBooleans = ["inline", "open", "range", "week-numbers", "fixed-weeks", "hide-adjacent", "today-button", "clear-button", "readonly", "required"];

	/** How many years a single page of the year view covers. */
	static #yearsPerPage = 12;

	/** The attributes that change the shell around the grid rather than the grid itself. */
	static #composedBy = [
		"inline", "label", "helper-text", "placeholder", "size", "variation", "name", "hidden-name",
		"hidden-id", "today-button", "clear-button", "open-label", "range", "readonly"
	];

	#defaultCulture = "en-GB";

	#view = "days";
	#displayed;
	#focused;
	#start = null;
	#end = null;

	/** Set while the component writes its own value attribute, so it doesn't answer its own change. */
	#writing = false;

	/** Set while focus is being handed back to the text box, which would otherwise reopen the panel. */
	#reopening = false;

	/**
	 * Tabbing out of the calendar puts it away. Focus landing nowhere is left alone, since that is
	 * what pressing the panel's own background does and it shouldn't close on that. Held as a field
	 * so the same listener can be taken off again when the calendar is rebuilt.
	 */
	#OnFocusOut = ev => {
		if (this.open && ev.relatedTarget && !this.contains(ev.relatedTarget)) { this.Close(); }
	};

	#elements = {};

	/**
	 * What the culture and the attributes work out to, held between redraws. Every cell asks the
	 * same questions, and building a formatter or reading week information for each one costs far
	 * more than the grid itself.
	 */
	#cache = {};

	get #today() {
		const now = new Date(Date.now());

		return new Date(now.getFullYear(), now.getMonth(), now.getDate());
	}

	/**
	 * The date the calendar has selected, or the start of the range when it is picking one.
	 */
	get valueAsDate() {
		return this.#start ? new Date(this.#start) : null;
	}

	set valueAsDate(value) {
		this.#Apply(this.#Parse(value), null);
	}

	get startDate() {
		return this.#start ? new Date(this.#start) : null;
	}

	get endDate() {
		return this.#end ? new Date(this.#end) : null;
	}

	/** The month on show, which isn't necessarily the month holding the value. */
	get displayedMonth() {
		return new Date(this.#displayed);
	}

	/** Which of the three views the calendar is on: days, months or years. */
	get view() {
		return this.#view;
	}

	constructor() {
		super();

		this.InitializeValidationMessages();

		this.culture = this.lang || document.documentElement.dataset.culture || navigator.language || this.#defaultCulture;

		if (this.closest("form") && !this.hiddenName && !this.name) { this.hiddenName = this.id; }

		this.#ReadValue();

		this.#displayed = this.#StartOfMonth(this.#Parse(this.month) ?? this.#start ?? this.#today);
		this.#focused = this.#start ?? (this.#IsSameMonth(this.#displayed, this.#today) ? this.#today : this.#displayed);
	}

	/* Dates ---------------------------------------------------------------------------------- */

	/**
	 * Reads a date without letting the time zone move it. A bare `yyyy-MM-dd` is taken as UTC by
	 * JavaScript, which lands on the day before for anywhere sat behind it, so the parts are pulled
	 * out and rebuilt locally instead.
	 */
	#Parse(value) {
		if (value instanceof Date) { return isNaN(value) ? null : new Date(value.getFullYear(), value.getMonth(), value.getDate()); }

		const text = value?.toString().trim();

		if (!text) { return null; }
		if (text.toLowerCase() === "today") { return this.#today; }

		const parts = /^(\d{4})-(\d{1,2})(?:-(\d{1,2}))?$/.exec(text);

		if (parts) {
			const [, year, month, day] = parts;

			return new Date(Number(year), Number(month) - 1, Number(day ?? 1));
		}

		const parsed = new Date(text);

		return isNaN(parsed) ? null : new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
	}

	#Key(date) {
		return `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, "0")}-${`${date.getDate()}`.padStart(2, "0")}`;
	}

	#AddDays(date, amount) {
		return new Date(date.getFullYear(), date.getMonth(), date.getDate() + amount);
	}

	/**
	 * Held to the end of the shorter month, so the 31st stepping into a month without one lands on
	 * its last day rather than spilling into the month after.
	 */
	#AddMonths(date, amount) {
		const moved = new Date(date.getFullYear(), date.getMonth() + amount, 1);

		return new Date(moved.getFullYear(), moved.getMonth(), Math.min(date.getDate(), this.#DaysInMonth(moved)));
	}

	#StartOfMonth(date) {
		return new Date(date.getFullYear(), date.getMonth(), 1);
	}

	#DaysInMonth(date) {
		return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
	}

	#IsSameDay(first, second) {
		return !!first && !!second && first.getTime() === second.getTime();
	}

	#IsSameMonth(first, second) {
		return !!first && !!second && first.getFullYear() === second.getFullYear() && first.getMonth() === second.getMonth();
	}

	#StartOfWeek(date) {
		return this.#AddDays(date, -((date.getDay() - this.#week.firstDay + 7) % 7));
	}

	/** The ISO-8601 week a date belongs to, which is the week holding its Thursday. */
	#WeekNumber(date) {
		const thursday = this.#AddDays(date, 3 - ((date.getDay() + 6) % 7)),
			january4th = new Date(thursday.getFullYear(), 0, 4);

		return 1 + Math.round((thursday - this.#AddDays(january4th, 3 - ((january4th.getDay() + 6) % 7))) / 604800000);
	}

	#MonthsBetween(from, to) {
		return ((to.getFullYear() - from.getFullYear()) * 12) + (to.getMonth() - from.getMonth());
	}

	/** The first year on a page of the year view, lined up so the pages don't drift. */
	#PageStart(date) {
		return date.getFullYear() - (date.getFullYear() % Calendar.#yearsPerPage);
	}

	/* Culture -------------------------------------------------------------------------------- */

	/**
	 * Where the culture puts the week. Browsers disagree on how this is reached and the older ones
	 * don't offer it at all, so a Monday start and a Saturday to Sunday weekend stand in.
	 */
	get #week() {
		this.#cache.week ??= (() => {
			let info = {};

			try {
				const locale = new Intl.Locale(this.culture);

				info = locale.getWeekInfo?.() ?? locale.weekInfo ?? {};
			} catch { /* An unusable culture is left to the fallbacks below. */ }

			const given = this.firstDay?.toString().trim();

			return {
				// Counted as getDay() counts, where Sunday is 0. ISO numbers the days 1 to 7 from
				// Monday, and 0 is taken as Sunday too since that's how the same number reads here.
				firstDay: (given && !isNaN(given) ? Number(given) : info.firstDay ?? 1) % 7,
				weekend: new Set((info.weekend ?? [6, 7]).map(day => day % 7))
			};
		})();

		return this.#cache.week;
	}

	get #format() {
		this.#cache.format ??= (() => {
			const build = culture => {
				const date = options => new Intl.DateTimeFormat(culture, options),
					weekday = ["narrow", "short", "long"].includes(this.weekdayFormat) ? this.weekdayFormat : "short";

				return {
					caption: date({ month: "long", year: "numeric" }),
					weekday: date({ weekday }),
					weekdayLong: date({ weekday: "long" }),
					day: date({ day: "numeric" }),
					full: date({ dateStyle: "full" }),
					month: date({ month: "long" }),
					monthShort: date({ month: "short" }),
					// What the text box shows, padded so it lines up with the hint sat behind it.
					field: date({ year: "numeric", month: "2-digit", day: "2-digit" }),
					// Years are counted rather than measured, so they're written without a thousands separator.
					number: new Intl.NumberFormat(culture, { useGrouping: false })
				};
			};

			try {
				return build(this.culture);
			} catch {
				// A culture Intl won't accept would otherwise take the whole grid down with it.
				return build(this.#defaultCulture);
			}
		})();

		return this.#cache.format;
	}

	/**
	 * How the culture lays a date out, which is the order the text box reads typed numbers in and
	 * the shape of the hint it shows while it's empty.
	 */
	get #pattern() {
		this.#cache.pattern ??= (() => {
			// A date with parts that can't be mistaken for one another, so the order is unambiguous.
			const parts = this.#format.field.formatToParts(new Date(2026, 10, 25)),
				fields = ["day", "month", "year"];

			return {
				order: parts.filter(part => fields.includes(part.type)).map(part => part.type),
				// Each part swapped for what it stands for, so whatever sits between them is kept
				// where the culture puts it rather than being repeated between every field.
				hint: parts.map(part => fields.includes(part.type) ? Localizer[`CALENDAR_PLACEHOLDER_${part.type.toUpperCase()}`] : part.value).join("")
			};
		})();

		return this.#cache.pattern;
	}

	get #placeholder() {
		return this.placeholder ?? this.#pattern.hint;
	}

	/**
	 * The words the calendar can't work out from the culture. An attribute wins, so a single
	 * calendar can be worded differently, and the Localizer covers the rest of the application.
	 */
	get #labels() {
		return {
			previousMonth: this.previousLabel || Localizer.CALENDAR_PREVIOUS_MONTH,
			nextMonth: this.nextLabel || Localizer.CALENDAR_NEXT_MONTH,
			previousYear: Localizer.CALENDAR_PREVIOUS_YEAR,
			nextYear: Localizer.CALENDAR_NEXT_YEAR,
			previousYears: Localizer.CALENDAR_PREVIOUS_YEARS,
			nextYears: Localizer.CALENDAR_NEXT_YEARS,
			today: this.todayLabel || Localizer.CALENDAR_TODAY,
			clear: this.clearLabel || Localizer.CALENDAR_CLEAR,
			week: this.weekLabel || Localizer.CALENDAR_WEEK,
			open: this.openLabel || Localizer.CALENDAR_OPEN,
			panel: Localizer.CALENDAR_PANEL,
			chooseMonth: this.chooseMonthLabel || Localizer.CALENDAR_CHOOSE_MONTH,
			chooseYear: this.chooseYearLabel || Localizer.CALENDAR_CHOOSE_YEAR,
			rangeStart: Localizer.CALENDAR_RANGE_START,
			rangeEnd: Localizer.CALENDAR_RANGE_END
		};
	}

	/* Availability --------------------------------------------------------------------------- */

	get #limits() {
		this.#cache.limits ??= {
			min: this.#Parse(this.min),
			max: this.#Parse(this.max),
			days: (this.disabledDays?.toString().toLowerCase().split(/[\s,]+/).filter(Boolean) ?? [])
				.reduce((days, entry) => {
					if (entry === "weekend") { this.#week.weekend.forEach(day => days.add(day)); }
					else if (!isNaN(entry)) { days.add(Number(entry) % 7); }

					return days;
				}, new Set()),
			// Each entry is either a single date or a `from..to` span.
			dates: (this.disabledDates?.toString().split(/[\s,]+/).filter(Boolean) ?? [])
				.map(entry => entry.split("..").map(part => this.#Parse(part)))
				.filter(([from]) => from)
				.map(([from, to]) => [from, to ?? from])
		};

		return this.#cache.limits;
	}

	#IsDisabled(date) {
		const { min, max, days, dates } = this.#limits;

		if (min && date < min) { return true; }
		if (max && date > max) { return true; }
		if (days.has(date.getDay())) { return true; }

		return dates.some(([from, to]) => date >= from && date <= to);
	}

	/** Whether the view has anywhere left to go, so the arrows can turn themselves off. */
	#CanStep(direction) {
		const boundary = direction < 0 ? this.#limits.min : this.#limits.max;

		if (!boundary) { return true; }

		const stepped = this.#Step(direction);

		return direction < 0 ? this.#EndOfView(stepped) >= boundary : this.#StartOfView(stepped) <= boundary;
	}

	#StartOfView(date) {
		switch (this.#view) {
			case "months": return new Date(date.getFullYear(), 0, 1);
			case "years": return new Date(this.#PageStart(date), 0, 1);
			default: return this.#StartOfMonth(date);
		}
	}

	#EndOfView(date) {
		switch (this.#view) {
			case "months": return new Date(date.getFullYear(), 11, 31);
			case "years": return new Date(this.#PageStart(date) + Calendar.#yearsPerPage - 1, 11, 31);
			default: return new Date(date.getFullYear(), date.getMonth(), this.#DaysInMonth(date));
		}
	}

	/** Whether a whole month or year sits outside the range the calendar allows. */
	#Exhausted(period, months) {
		const { min, max } = this.#limits,
			first = months ? this.#StartOfMonth(period) : new Date(period.getFullYear(), 0, 1),
			last = months ? new Date(period.getFullYear(), period.getMonth(), this.#DaysInMonth(period)) : new Date(period.getFullYear(), 11, 31);

		return (min && last < min) || (max && first > max);
	}

	/* Composition ---------------------------------------------------------------------------- */

	parsedCallback() {
		this.#Compose();
		this.#Normalise();
	}

	/**
	 * Builds everything around the grid, which is either a text box the grid drops out of or the
	 * grid on its own when the calendar has been asked to sit inline.
	 */
	#Compose() {
		this.innerHTML = "";
		this.#elements = {};
		this.removeEventListener("focusout", this.#OnFocusOut);

		// A text box carries its own label and helper text, so those are only ours to draw when the
		// grid is sat on the page by itself.
		if (this.inline) {
			if (this.label) { this.appendChild(this.#BuildLabel()); }

			this.appendChild(this.#BuildGrid());

			if (this.helperText) {
				this.appendChild(document.createElementWithContents("div", this.helperText, { className: "input-field__helper-text" }));
			}
		} else {
			this.appendChild(this.#BuildField());
		}

		const posted = this.hiddenName || this.name;

		if (posted || this.hiddenId) {
			this.#elements.hidden = document.createElementWithContents("input", null, {
				type: "hidden",
				value: this.#Serialise()
			});

			if (posted) { this.#elements.hidden.name = posted; }
			if (this.hiddenId) { this.#elements.hidden.id = this.hiddenId; }

			this.appendChild(this.#elements.hidden);
		}

		this.#Render();

		// The caption's hint is written by the render above, and it only needs wiring the once since
		// the tooltip is read from the attribute as it's shown.
		Razor.RefreshTooltips();

		// A calendar asked to start open still has to be put in place and announced as being open.
		if (this.open && !this.inline) { this.#Reveal(); }
	}

	/**
	 * Names the grid an inline calendar shows. There is nothing to point a label at, so it is read
	 * out through the grid rather than tied to a control.
	 */
	#BuildLabel() {
		return document.createElementWithContents("div", this.label, {
			className: "input-field__label",
			id: `lb${this.identifier}`
		});
	}

	/**
	 * The grid and the header and footer around it. These are held onto rather than rebuilt, since
	 * the buttons are components of their own and would sit waiting to parse again on every step
	 * through the months.
	 */
	#BuildGrid() {
		const labels = this.#labels,
			calendar = document.createElementWithContents("div", null, { className: "calendar" });

		this.#elements.previous = this.#BuildNav("fas fa-chevron-left", -1);
		this.#elements.next = this.#BuildNav("fas fa-chevron-right", 1);

		this.#elements.caption = document.createElementWithContents("button", null, {
			className: "calendar__caption",
			type: "button",
			onclick: () => this.#CycleView()
		});

		// Read out as it changes, so stepping through the months says where the grid has landed.
		this.#elements.caption.setAttribute("aria-live", "polite");

		calendar.appendChild(document.createElementWithContents("div", [this.#elements.previous, this.#elements.caption, this.#elements.next], {
			className: "calendar__header"
		}));

		this.#elements.views = document.createElementWithContents("div", null, {
			className: "calendar__views",
			onkeydown: ev => this.#HandleKeydown(ev),
			onmouseleave: () => this.#PreviewRange(null)
		});

		calendar.appendChild(this.#elements.views);

		if (this.todayButton || this.clearButton) {
			const footer = document.createElementWithContents("div", null, { className: "calendar__footer" });

			if (this.todayButton) {
				footer.appendChild(this.#elements.todayButton = this.#BuildAction(labels.today, () => this.Today()));
			}

			if (this.clearButton) {
				footer.appendChild(this.#elements.clearButton = this.#BuildAction(labels.clear, () => this.Clear()));
			}

			calendar.appendChild(footer);
		}

		return calendar;
	}

	/**
	 * The text box the calendar drops out of. This is a chip-input rather than an input of our own,
	 * so it picks up everything a field is supposed to have, from its measurements and its label
	 * through to the way it reports itself invalid, and stands beside the other fields on a form
	 * looking like one of them.
	 */
	#BuildField() {
		const labels = this.#labels,
			panel = `pn${this.identifier}`,
			holder = document.createElement("div");

		// Written out as markup rather than created outright, because a component's constructor is
		// not allowed to give itself attributes, and an input's settles a tabindex on the way past.
		// Anything the input reads while it is being built has to be here rather than assigned after.
		holder.innerHTML = `<chip-input class="calendar__field"${this.required ? " required" : ""}></chip-input>`;

		// A range is shown as two dates, which is more than anybody should be asked to type, so
		// those boxes only read them out and the grid does the picking.
		const field = Object.assign(holder.firstElementChild, {
			label: this.label,
			helperText: this.helperText,
			placeholder: this.#placeholder,
			value: this.#Display(),
			suffixButton: "far fa-calendar-alt",
			suffixTooltip: labels.open,
			readonly: this.range || this.readonly,
			onsuffixclick: () => this.Toggle(),
			// The box keeps its own change to itself, so the only one to leave the calendar is the
			// one raised once a date has actually been taken on.
			onchange: () => this.#CommitTyped()
		});

		if (this.size) { field.size = this.size; }
		if (this.variation) { field.variation = this.variation; }

		// Read when the box validates, which is long after its constructor had its look at the
		// attributes, so the message is handed over rather than set as one.
		field.VALIDATION_REQUIRED = this.VALIDATION_REQUIRED || Localizer.CALENDAR_REQUIRED;

		// Taken from the box itself rather than the whole field, since the button on the end takes
		// focus when it is pressed and would open the panel its own press then closes.
		field.addEventListener("focusin", ev => {
			if (ev.target.nodeName === "INPUT" && !this.#reopening) { this.Open(); }
		});

		field.addEventListener("keydown", ev => this.#HandleFieldKeydown(ev));

		this.addEventListener("focusout", this.#OnFocusOut);

		this.#elements.popover = document.createElementWithContents("div", this.#BuildGrid(), {
			className: "calendar__popover",
			id: panel
		});

		this.#elements.popover.setAttribute("role", "dialog");
		this.#elements.popover.setAttribute("aria-label", this.label || labels.panel);

		// The box builds itself a frame later than this, so anything of its own that the calendar
		// needs a hand in has to wait until it has.
		field.onparsed = () => {
			this.#elements.input = field.querySelector("input");

			Object.entries({
				role: "combobox",
				"aria-haspopup": "dialog",
				"aria-expanded": this.open ? "true" : "false",
				"aria-controls": panel
			}).forEach(([name, value]) => this.#elements.input.setAttribute(name, value));

			this.#elements.input.disabled = this.disabled;
			this.#SetDisabled(field.querySelector("chip-button.input__bound"), this.disabled || this.readonly);

			// Hung inside the box's own wrapper, which is what the panel is measured and placed against.
			field.querySelector(".input__wrapper").appendChild(this.#elements.popover);

			if (this.open) { this.#Reposition(); }
		};

		return this.#elements.field = field;
	}

	#BuildNav(icon, direction) {
		return document.createElementWithContents("chip-button", null, {
			className: "calendar__nav",
			buttonStyle: "icon",
			variation: "body",
			size: "sm",
			icon,
			onclick: () => this.Navigate(direction)
		});
	}

	#BuildAction(text, onclick) {
		return document.createElementWithContents("chip-button", text, {
			className: "calendar__action",
			variation: "body",
			size: "sm",
			onclick
		});
	}

	/**
	 * Turns one of the buttons around the grid off. A button parses after the calendar that built it
	 * and settles its own disabled state as it goes, so anything said beforehand has to be said again
	 * once it has finished.
	 */
	#SetDisabled(button, disabled) {
		if (!button) { return; }

		if (button.parsed) { button.disabled = disabled; }
		else { button.onparsed = () => button.disabled = disabled; }
	}

	/* Rendering ------------------------------------------------------------------------------ */

	/**
	 * Redraws the header and whichever view is showing. Focus is handed back afterwards, since
	 * arrowing onto a date in another month replaces the cell the keyboard was sat on.
	 */
	#Render() {
		if (!this.#elements.views) { return; }

		this.#cache = {};

		const labels = this.#labels,
			// Only the grid gets focus handed back. Anywhere else in the calendar, the text box in
			// particular, has to be left where it is or a redraw would pull the caret out of it.
			hadFocus = this.#elements.views.contains(document.activeElement);

		// The caption zooms the view out, and the years are as far out as it goes.
		this.#elements.caption.disabled = this.#view === "years";

		switch (this.#view) {
			case "months":
				this.#elements.caption.textContent = this.#format.number.format(this.#displayed.getFullYear());
				this.#elements.caption.setAttribute("tooltip", labels.chooseYear);
				this.#elements.previous.description = labels.previousYear;
				this.#elements.next.description = labels.nextYear;
				break;
			case "years":
				this.#elements.caption.textContent = [this.#PageStart(this.#displayed), this.#PageStart(this.#displayed) + Calendar.#yearsPerPage - 1]
					.map(year => this.#format.number.format(year))
					.join(" – ");

				this.#elements.caption.removeAttribute("tooltip");
				this.#elements.previous.description = labels.previousYears;
				this.#elements.next.description = labels.nextYears;
				break;
			default:
				this.#elements.caption.textContent = this.#format.caption.format(this.#displayed);
				this.#elements.caption.setAttribute("tooltip", labels.chooseMonth);
				this.#elements.previous.description = labels.previousMonth;
				this.#elements.next.description = labels.nextMonth;
				break;
		}

		this.#SetDisabled(this.#elements.previous, !this.#CanStep(-1));
		this.#SetDisabled(this.#elements.next, !this.#CanStep(1));
		this.#SetDisabled(this.#elements.todayButton, this.#IsDisabled(this.#today));
		this.#SetDisabled(this.#elements.clearButton, !this.#start);

		this.dataset.view = this.#view;

		this.#elements.views.innerHTML = "";
		this.#elements.views.appendChild(this.#view === "days" ? this.#BuildDays() : this.#BuildPeriods());

		this.#RefreshDisplay();

		if (hadFocus) { this.#FocusCell(); }
	}

	#BuildDays() {
		const table = document.createElementWithContents("table", null, { className: "calendar__grid" }),
			headings = document.createElementWithContents("tr"),
			body = document.createElementWithContents("tbody"),
			first = this.#StartOfMonth(this.#displayed),
			start = this.#StartOfWeek(first);

		table.setAttribute("role", "grid");
		headings.setAttribute("role", "row");

		if (this.label) { table.setAttribute("aria-labelledby", `lb${this.identifier}`); }

		if (this.weekNumbers) {
			headings.appendChild(document.createElementWithContents("th", `<span class="visually-hidden">${this.#labels.week}</span>`, {
				className: "calendar__week-heading",
				scope: "col"
			}));
		}

		for (let day = 0; day < 7; day++) {
			const date = this.#AddDays(start, day),
				full = this.#format.weekdayLong.format(date),
				heading = document.createElementWithContents("th", `<span aria-hidden="true">${this.#format.weekday.format(date)}</span><span class="visually-hidden">${full}</span>`, {
					className: "calendar__weekday",
					scope: "col"
				});

			heading.setAttribute("abbr", full);

			if (this.#week.weekend.has(date.getDay())) { heading.classList.add("calendar__weekday--weekend"); }

			headings.appendChild(heading);
		}

		// Six rows only when they've been asked for, so a short month doesn't grow a blank week.
		const weeks = this.fixedWeeks
			? 6
			: Math.ceil((Math.round((first - start) / 86400000) + this.#DaysInMonth(first)) / 7);

		for (let week = 0; week < weeks; week++) {
			const row = document.createElementWithContents("tr");

			row.setAttribute("role", "row");

			if (this.weekNumbers) {
				row.appendChild(document.createElementWithContents("th", this.#format.number.format(this.#WeekNumber(this.#AddDays(start, week * 7))), {
					className: "calendar__week",
					scope: "row"
				}));
			}

			for (let day = 0; day < 7; day++) {
				row.appendChild(this.#BuildDay(this.#AddDays(start, (week * 7) + day)));
			}

			body.appendChild(row);
		}

		table.appendChild(document.createElementWithContents("thead", headings));
		table.appendChild(body);

		return table;
	}

	#BuildDay(date) {
		const cell = document.createElementWithContents("td", null, { className: "calendar__cell" }),
			outside = !this.#IsSameMonth(date, this.#displayed),
			unavailable = this.#IsDisabled(date);

		cell.setAttribute("role", "gridcell");
		cell.dataset.date = this.#Key(date);

		if (outside && this.hideAdjacent) {
			cell.classList.add("calendar__cell--empty");

			return cell;
		}

		const day = document.createElementWithContents("button", `<span aria-hidden="true">${this.#format.day.format(date)}</span>`, {
			className: "calendar__day",
			type: "button",
			disabled: unavailable || this.disabled,
			tabIndex: this.#IsSameDay(date, this.#focused) ? 0 : -1,
			onclick: () => this.Select(date)
		}),
			described = [this.#format.full.format(date)];

		if (this.range && this.#end) {
			if (this.#IsSameDay(date, this.#start)) { described.push(this.#labels.rangeStart); }
			if (this.#IsSameDay(date, this.#end)) { described.push(this.#labels.rangeEnd); }
		}

		day.setAttribute("aria-label", described.join(", "));

		if (outside) { cell.classList.add("calendar__cell--outside"); }
		if (unavailable) { cell.classList.add("calendar__cell--disabled"); }
		if (this.#week.weekend.has(date.getDay())) { cell.classList.add("calendar__cell--weekend"); }

		if (this.#IsSameDay(date, this.#today)) {
			cell.classList.add("calendar__cell--today");
			day.setAttribute("aria-current", "date");
		}

		if (this.#IsSameDay(date, this.#start) || this.#IsSameDay(date, this.#end)) {
			cell.classList.add("calendar__cell--selected");
			cell.setAttribute("aria-selected", "true");
		}

		if (this.range) {
			if (this.#end && this.#IsSameDay(date, this.#start)) { cell.classList.add("calendar__cell--range-start"); }
			if (this.#IsSameDay(date, this.#end)) { cell.classList.add("calendar__cell--range-end"); }
			if (this.#end && date > this.#start && date < this.#end) { cell.classList.add("calendar__cell--within"); }

			// Only worth shading a range that's still waiting on its second date.
			if (this.#start && !this.#end) { day.onmouseenter = () => this.#PreviewRange(date); }
		}

		cell.appendChild(day);

		return cell;
	}

	/**
	 * The month and year views, which are the same set of buttons over a different run of periods.
	 */
	#BuildPeriods() {
		const months = this.#view === "months",
			labels = this.#labels,
			list = document.createElementWithContents("div", null, { className: `calendar__periods calendar__periods--${this.#view}` });

		list.setAttribute("role", "group");
		list.setAttribute("aria-label", months ? labels.chooseMonth : labels.chooseYear);

		const periods = months
			? Array.from({ length: 12 }, (_, month) => new Date(this.#displayed.getFullYear(), month, 1))
			: Array.from({ length: Calendar.#yearsPerPage }, (_, offset) => new Date(this.#PageStart(this.#displayed) + offset, this.#displayed.getMonth(), 1));

		periods.forEach(period => {
			const focused = months
				? this.#IsSameMonth(period, this.#focused)
				: period.getFullYear() === this.#focused.getFullYear();

			const selected = months
				? this.#IsSameMonth(period, this.#start)
				: period.getFullYear() === this.#start?.getFullYear();

			const unavailable = this.#Exhausted(period, months),
				button = document.createElementWithContents("button", months ? this.#format.monthShort.format(period) : this.#format.number.format(period.getFullYear()), {
					className: "calendar__period",
					type: "button",
					disabled: unavailable || this.disabled,
					tabIndex: focused ? 0 : -1,
					onclick: () => this.#ChoosePeriod(period)
				});

			if (months) { button.setAttribute("aria-label", this.#format.month.format(period)); }

			if (selected) {
				button.classList.add("calendar__period--selected");
				button.setAttribute("aria-current", "true");
			}

			if (months ? this.#IsSameMonth(period, this.#today) : period.getFullYear() === this.#today.getFullYear()) {
				button.classList.add("calendar__period--current");
			}

			list.appendChild(button);
		});

		return list;
	}

	/* The text box ---------------------------------------------------------------------------- */

	/** What the text box reads, which is a date in the culture's own layout. */
	#Display() {
		return [this.#start, this.#end].filter(Boolean).map(date => this.#format.field.format(date)).join(Localizer.CALENDAR_RANGE_JOIN);
	}

	/**
	 * Writes the date into the box. This goes through the field rather than the input inside it, so
	 * the box's own idea of its value keeps up with what the calendar is showing.
	 */
	#RefreshDisplay() {
		const display = this.#Display();

		if (this.#elements.field) { this.#elements.field.value = display; }

		// Written to the box itself as well, since the field only passes a value along when it sees
		// one it hasn't got, and what has been typed since is only ever on the box.
		if (this.#elements.input && this.#elements.input.value !== display) { this.#elements.input.value = display; }
	}

	/**
	 * Reads a typed date the way the culture writes them, so numbers land in the fields somebody
	 * looking at the hint would expect them to. A four figure year is taken as written, and two
	 * figures are read the way most systems do, where 69 is 2069 and 70 is 1970.
	 */
	#ParseTyped(text) {
		const typed = text.trim().toLowerCase();

		if (!typed) { return null; }

		// The same shortcuts a date input takes, since they save reaching for the numbers at all.
		if (["t", "today"].includes(typed)) { return this.#today; }
		if (typed === "y") { return this.#AddDays(this.#today, -1); }

		// An ISO date is understood whatever the culture, as that's the form the value carries.
		if (/^\d{4}-\d{1,2}(-\d{1,2})?$/.test(typed)) { return this.#Parse(typed); }

		const { order } = this.#pattern,
			fields = {};

		let groups = typed.match(/\d+/g) ?? [];

		if (groups.length === 1 && groups[0].length <= 2) {
			// A number on its own is a day of the month already on show.
			fields.day = Number(groups[0]);
		} else {
			// Typed without anything between the numbers, so the run of digits is cut into the
			// fields the culture expects, taking the year as either four figures or two.
			if (groups.length === 1 && [6, 8].includes(groups[0].length)) {
				const digits = groups[0];

				let taken = 0;

				groups = order.map(field => {
					const width = field === "year" ? digits.length - 4 : 2;

					return digits.slice(taken, taken += width);
				});
			}

			if (groups.length < 2 || groups.length > 3) { return null; }

			// Two numbers are the day and the month, leaving the year as the one on show.
			(groups.length === 3 ? order : order.filter(field => field !== "year"))
				.forEach((field, index) => fields[field] = Number(groups[index]));
		}

		if (!fields.day) { return null; }

		const year = fields.year == null
			? this.#displayed.getFullYear()
			: fields.year < 100 ? (fields.year <= 69 ? 2000 : 1900) + fields.year : fields.year;

		const month = (fields.month ?? this.#displayed.getMonth() + 1) - 1,
			date = new Date(year, month, fields.day);

		// Turned down rather than rolled over, so the 31st of a short month isn't quietly moved on.
		return date.getFullYear() === year && date.getMonth() === month && date.getDate() === fields.day ? date : null;
	}

	/**
	 * Takes on whatever has been typed. Anything unreadable, or a date the calendar isn't allowing,
	 * puts the box back to the date it still holds rather than leaving something that isn't true.
	 */
	#CommitTyped() {
		const field = this.#elements.field;

		if (!field || this.range || this.readonly || this.disabled) { return; }

		// Read from the box rather than the field around it, as the box is what has been typed into.
		const typed = ((this.#elements.input ?? field).value ?? "").trim();

		if (typed === this.#Display()) { return; }
		if (!typed) { return this.Clear(); }

		this.#cache = {};

		const date = this.#ParseTyped(typed);

		if (!date || this.#IsDisabled(date)) { return this.#RefreshDisplay(); }

		this.Select(date);
	}

	#HandleFieldKeydown(ev) {
		switch (ev.key) {
			case "Escape":
				if (this.open) {
					ev.preventDefault();
					this.Close();
				}
				break;
			case "ArrowDown":
			case "ArrowUp":
				// Straight into the grid, which is where the arrows are worth having.
				ev.preventDefault();
				this.Open();
				this.#FocusCell();
				break;
			case "Enter":
				this.#CommitTyped();
				this.Close();
				break;
		}
	}

	/**
	 * Puts focus back in the box without the box taking that as being asked to open again.
	 */
	#Refocus() {
		this.#reopening = true;
		this.#elements.input?.focus();
		this.#reopening = false;
	}

	/**
	 * Works out which way the panel has room to go. It is measured where it sits, so this only runs
	 * once the panel is on show.
	 */
	#Reposition() {
		const popover = this.#elements.popover;

		if (!popover?.parentElement) { return; }

		popover.removeClass("calendar__popover--above");
		popover.removeClass("calendar__popover--end");

		const field = popover.parentElement.getBoundingClientRect(),
			panel = popover.getBoundingClientRect();

		if (field.bottom + panel.height > window.innerHeight && field.top - panel.height > 0) {
			popover.classList.add("calendar__popover--above");
		}

		if (panel.right > window.innerWidth || panel.left < 0) {
			popover.classList.add("calendar__popover--end");
		}
	}

	#Reveal() {
		this.#view = "days";
		this.#focused = this.#start ?? (this.#IsSameMonth(this.#displayed, this.#today) ? this.#today : this.#displayed);

		if (this.#start) { this.#displayed = this.#StartOfMonth(this.#start); }

		this.#Render();
		this.#Reposition();

		this.#elements.input?.setAttribute("aria-expanded", "true");
		this.raiseEvent("opened");
	}

	#Conceal() {
		this.#elements.popover?.removeClass("calendar__popover--above");
		this.#elements.popover?.removeClass("calendar__popover--end");
		this.#elements.input?.setAttribute("aria-expanded", "false");

		this.raiseEvent("closed");
	}

	/* Interaction ---------------------------------------------------------------------------- */

	/**
	 * Shades the dates between the open end of a range and the date under the pointer. It only ever
	 * swaps classes, since rebuilding the grid on every movement of the mouse would be wasteful.
	 */
	#PreviewRange(date) {
		this.#elements.views.querySelectorAll(".calendar__cell--preview").forEach(cell => cell.removeClass("calendar__cell--preview"));

		if (!date || !this.#start || this.#end || date < this.#start) { return; }

		this.#elements.views.querySelectorAll("[data-date]").forEach(cell => {
			const cellDate = this.#Parse(cell.dataset.date);

			if (cellDate > this.#start && cellDate <= date) { cell.classList.add("calendar__cell--preview"); }
		});
	}

	#CycleView() {
		this.#view = this.#view === "days" ? "months" : this.#view === "months" ? "years" : "days";
		this.#focused = this.#displayed;

		this.#Render();
		this.#FocusCell();
	}

	#ChoosePeriod(period) {
		if (this.#view === "months") {
			this.#displayed = this.#StartOfMonth(period);
			this.#view = "days";
			this.#focused = this.#IsSameMonth(this.#displayed, this.#today) ? this.#today : this.#displayed;
		} else {
			this.#displayed = new Date(period.getFullYear(), this.#displayed.getMonth(), 1);
			this.#view = "months";
			this.#focused = this.#displayed;
		}

		this.#Render();
		this.#FocusCell();
		this.raiseEvent("navigate", { month: this.displayedMonth, view: this.#view });
	}

	/** Where a step in the current view would land, without moving there. */
	#Step(direction) {
		switch (this.#view) {
			case "months": return new Date(this.#displayed.getFullYear() + direction, this.#displayed.getMonth(), 1);
			case "years": return new Date(this.#displayed.getFullYear() + (direction * Calendar.#yearsPerPage), this.#displayed.getMonth(), 1);
			default: return this.#AddMonths(this.#displayed, direction);
		}
	}

	#FocusCell() {
		this.#elements.views?.querySelector("[tabindex='0']")?.focus();
	}

	#HandleKeydown(ev) {
		const moved = this.#view === "days" ? this.#DayMovement(ev) : this.#PeriodMovement(ev);

		if (moved) {
			ev.preventDefault();
			this.#MoveFocus(moved);

			return;
		}

		if (["Enter", " ", "Spacebar"].includes(ev.key)) {
			ev.preventDefault();

			if (this.#view === "days") { this.Select(this.#focused); }
			else { this.#ChoosePeriod(this.#focused); }
		}
	}

	#DayMovement(ev) {
		switch (ev.key) {
			case "ArrowLeft": return this.#AddDays(this.#focused, -1);
			case "ArrowRight": return this.#AddDays(this.#focused, 1);
			case "ArrowUp": return this.#AddDays(this.#focused, -7);
			case "ArrowDown": return this.#AddDays(this.#focused, 7);
			case "Home": return this.#StartOfWeek(this.#focused);
			case "End": return this.#AddDays(this.#StartOfWeek(this.#focused), 6);
			case "PageUp": return this.#AddMonths(this.#focused, ev.shiftKey ? -12 : -1);
			case "PageDown": return this.#AddMonths(this.#focused, ev.shiftKey ? 12 : 1);
			default: return null;
		}
	}

	#PeriodMovement(ev) {
		const months = this.#view === "months",
			step = amount => months ? this.#AddMonths(this.#focused, amount) : new Date(this.#focused.getFullYear() + amount, this.#focused.getMonth(), 1),
			page = amount => new Date(this.#focused.getFullYear() + (amount * (months ? 1 : Calendar.#yearsPerPage)), this.#focused.getMonth(), 1);

		switch (ev.key) {
			// Four to a row in the year view, three in the month view.
			case "ArrowLeft": return step(-1);
			case "ArrowRight": return step(1);
			case "ArrowUp": return step(months ? -3 : -4);
			case "ArrowDown": return step(months ? 3 : 4);
			case "Home": return months ? new Date(this.#focused.getFullYear(), 0, 1) : new Date(this.#PageStart(this.#focused), this.#focused.getMonth(), 1);
			case "End": return months ? new Date(this.#focused.getFullYear(), 11, 1) : new Date(this.#PageStart(this.#focused) + Calendar.#yearsPerPage - 1, this.#focused.getMonth(), 1);
			case "PageUp": return page(-1);
			case "PageDown": return page(1);
			default: return null;
		}
	}

	/**
	 * Moves the keyboard onto another date, bringing the view along when it has arrowed past the
	 * edge of what's on show.
	 */
	#MoveFocus(date) {
		const { min, max } = this.#limits;

		if (min && date < min) { date = min; }
		if (max && date > max) { date = max; }

		this.#focused = date;

		const showing = this.#view === "years"
			? this.#PageStart(date) === this.#PageStart(this.#displayed)
			: this.#view === "months"
				? date.getFullYear() === this.#displayed.getFullYear()
				: this.#IsSameMonth(date, this.#displayed);

		if (!showing) {
			this.#displayed = this.#view === "days" ? this.#StartOfMonth(date) : date;
			this.raiseEvent("navigate", { month: this.displayedMonth, view: this.#view });
		}

		this.#Render();
		this.#FocusCell();
	}

	/* Value ---------------------------------------------------------------------------------- */

	#ReadValue() {
		const [start, end] = (this.value ?? "").toString().split("|").map(part => this.#Parse(part));

		this.#start = start ?? null;
		this.#end = (this.range ? end : null) ?? null;

		if (this.#end && this.#end < this.#start) { [this.#start, this.#end] = [this.#end, this.#start]; }
	}

	#Serialise() {
		return [this.#start, this.#end].filter(Boolean).map(date => this.#Key(date)).join("|");
	}

	/**
	 * Takes dates on as the value without telling anybody. Being handed a value isn't the same as
	 * somebody choosing a date, so nothing is raised, just as a native input stays quiet.
	 */
	#Apply(start, end) {
		this.#start = start;
		this.#end = end;
		this.#focused = start ?? this.#focused;

		if (start) { this.#displayed = this.#StartOfMonth(start); }

		this.#Write();
		this.#Render();
	}

	#Write() {
		const serialised = this.#Serialise();

		this.#writing = true;
		this.value = serialised;
		this.#writing = false;

		if (this.#elements.hidden) { this.#elements.hidden.value = serialised; }
	}

	/**
	 * Puts what the element carries into the form the calendar writes, so a value given as `today` or
	 * a range written back to front settles down into plain dates in order.
	 */
	#Normalise() {
		if ((this.value ?? "") !== this.#Serialise()) { this.#Write(); }
		else if (this.#elements.hidden) { this.#elements.hidden.value = this.#Serialise(); }
	}

	/* API ------------------------------------------------------------------------------------ */

	/**
	 * Picks a date as though it had been clicked, filling the open end of a range when the calendar
	 * is picking one. Anything out of reach is turned down.
	 * @param {Date|string} value The date to pick.
	 */
	Select(value) {
		const date = this.#Parse(value);

		this.#cache = {};

		if (!date || this.readonly || this.disabled || this.#IsDisabled(date)) { return; }

		// Picking below the start, or picking again once a range is whole, starts the range over.
		if (!this.range || !this.#start || this.#end || date < this.#start) {
			this.#start = date;
			this.#end = null;
		} else {
			this.#end = date;
		}

		this.#focused = date;
		this.#displayed = this.#StartOfMonth(date);

		this.#Write();
		this.#Render();

		if (this.required) { this.reportValidity(); }

		if (this.range && !this.#end) { this.raiseEvent("rangestart", { date: this.startDate }); }
		else { this.raiseEvent("change", { date: this.valueAsDate, start: this.startDate, end: this.endDate, value: this.value }); }

		// A whole answer puts the panel away and hands the box back, so the flow ends where it began.
		// A range that is only half picked stays open, since it still wants its second date.
		if (this.open && (!this.range || this.#end)) {
			this.Close();
			this.#Refocus();
		}
	}

	/**
	 * Empties the calendar.
	 */
	Clear() {
		if (this.readonly || this.disabled) { return; }

		this.#Apply(null, null);

		if (this.required) { this.reportValidity(); }

		this.raiseEvent("change", { date: null, start: null, end: null, value: "" });
	}

	/**
	 * Shows the month holding a date, leaving the value alone.
	 * @param {Date|string} value A date in the month to show.
	 */
	GoTo(value) {
		const date = this.#Parse(value);

		if (!date) { return; }

		this.#displayed = this.#StartOfMonth(date);
		this.#focused = date;

		this.#Render();
		this.raiseEvent("navigate", { month: this.displayedMonth, view: this.#view });
	}

	/**
	 * Moves the view along, by a month, a year or a page of years depending on what it is showing.
	 * @param {number} direction How many steps to move, so -1 goes back one.
	 */
	Navigate(direction = 1) {
		this.#cache = {};

		if (!this.#CanStep(direction)) { return; }

		const previous = this.#displayed;

		this.#displayed = this.#Step(direction);
		this.#focused = this.#AddMonths(this.#focused, this.#MonthsBetween(previous, this.#displayed));

		this.#Render();
		this.raiseEvent("navigate", { month: this.displayedMonth, view: this.#view });
	}

	/**
	 * Picks today, which is what the footer's button does.
	 */
	Today() {
		this.#view = "days";
		this.Select(this.#today);
	}

	/**
	 * Drops the calendar out of the text box, showing the month holding whatever has been picked.
	 * An inline calendar is always on show, so this does nothing to one, and neither a disabled nor
	 * a readonly calendar opens either.
	 */
	Open() {
		if (this.inline || this.open || this.disabled || this.readonly) { return; }

		this.open = true;
	}

	/**
	 * Puts the calendar away again.
	 */
	Close() {
		if (this.open) { this.open = false; }
	}

	/**
	 * Opens the calendar, or closes it when it is already open.
	 */
	Toggle() {
		this.open ? this.Close() : this.Open();
	}

	/**
	 * Draws the calendar again, which is worth reaching for when what a date means to the
	 * application has changed rather than an attribute.
	 */
	Refresh() {
		this.#Render();
	}

	checkValidity() {
		const missing = this.required && !this.#start;

		this.validationMessage = missing ? (this.VALIDATION_REQUIRED || Localizer.CALENDAR_REQUIRED) : "";

		return !missing;
	}

	/**
	 * Marks the calendar as being in error, which the text box knows how to do for itself and an
	 * inline grid has done underneath it.
	 */
	setInvalid(message) {
		if (this.#elements.field) { return this.#elements.field.setInvalid(message); }

		this.querySelector(":scope > .input-field__validation-error")?.remove();
		this.querySelector(":scope > .calendar")?.classList.add("input-field__invalid");

		this.appendChild(document.createElementWithContents("div", message, { className: "input-field__validation-error" }));
	}

	reportValidity() {
		const validity = this.checkValidity();

		if (this.#elements.field) {
			// Clears whatever the box was showing before, then puts back the calendar's own reason
			// rather than the one the box would give for an empty field.
			this.#elements.field.reportValidity();
		} else {
			this.querySelector(":scope > .input-field__validation-error")?.remove();
			this.querySelectorAll(".input-field__invalid").forEach(element => element.removeClass("input-field__invalid"));
		}

		if (!validity) { this.setInvalid(this.validationMessage); }

		return validity;
	}

	focus() {
		if (this.#elements.field) { this.#elements.field.focus(); }
		else { this.#FocusCell(); }
	}

	blur() {
		this.#elements.field?.blur();
		this.#elements.views?.querySelector("[tabindex='0']")?.blur();
	}

	attributeChanged(name, _, newValue) {
		if (!this.#elements.views || this.#writing) { return; }

		if (name === "open") {
			// The attribute is the switch for the panel, so setting it by hand works just as well as
			// calling Open, and both of these draw what they need themselves. An inline calendar has
			// no panel to work, and is left alone.
			if (!this.inline) { this.IsAttributeValueTrue(newValue) ? this.#Reveal() : this.#Conceal(); }

			return;
		}

		switch (name) {
			case "value":
				this.#ReadValue();

				this.#focused = this.#start ?? this.#focused;
				this.#displayed = this.#StartOfMonth(this.#start ?? this.#displayed);

				this.#Normalise();
				break;
			case "month":
				this.#displayed = this.#StartOfMonth(this.#Parse(newValue) ?? this.#displayed);
				this.#focused = this.#displayed;
				break;
			case "lang":
				this.culture = newValue || document.documentElement.dataset.culture || navigator.language || this.#defaultCulture;
				break;
			case "range":
				this.#ReadValue();
				break;
			case "disabled":
				if (this.#elements.input) { this.#elements.input.disabled = this.disabled; }

				this.#SetDisabled(this.#elements.field?.querySelector("chip-button.input__bound"), this.disabled || this.readonly);

				if (this.disabled) { this.Close(); }
				break;
			case "required":
				if (this.#elements.field) { this.#elements.field.required = this.required; }
				break;
		}

		if (Calendar.#composedBy.includes(name)) { this.#Compose(); }
		else { this.#Render(); }
	}
}
